import { Calendar } from 'lucide-react';

type CalendarDateInputProps = {
  value: string;
  onChange: (value: string) => void;
  id?: string;
  min?: string;
  max?: string;
  'aria-label'?: string;
  className?: string;
};

function openPicker(input: HTMLInputElement | null) {
  if (!input) return;
  // showPicker needs a user gesture and is missing in some WebViews — fall
  // back to focusing the field so the native control still works.
  try {
    if (typeof input.showPicker === 'function') {
      input.showPicker();
      return;
    }
  } catch {
    /* user-gesture or cross-origin restriction: fall through */
  }
  input.focus();
}

/**
 * A native date box with a calendar button you can actually see (Jim
 * 2026-09-15: "need add a calendar on these date boxes… there is a date box
 * just cant see it very well"). The browser's own picker indicator is hidden
 * and replaced by a proper button; clicking anywhere on the field opens the
 * calendar too. Values are plain YYYY-MM-DD strings.
 */
export function CalendarDateInput({
  value,
  onChange,
  id,
  min,
  max,
  'aria-label': ariaLabel,
  className = '',
}: CalendarDateInputProps) {
  let inputEl: HTMLInputElement | null = null;
  return (
    <div className={`relative flex items-center ${className}`}>
      <input
        ref={(el) => {
          inputEl = el;
        }}
        id={id}
        type="date"
        value={value}
        min={min}
        max={max}
        onChange={(e) => onChange(e.target.value)}
        onClick={(e) => openPicker(e.currentTarget)}
        aria-label={ariaLabel}
        className="w-full pl-3 pr-10 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent cursor-pointer [&::-webkit-calendar-picker-indicator]:hidden"
      />
      <button
        type="button"
        tabIndex={-1}
        onClick={() => openPicker(inputEl)}
        aria-label="Open calendar"
        title="Open calendar"
        className="absolute right-1.5 inline-flex items-center justify-center w-7 h-7 rounded-md text-slate-500 hover:text-primary-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-600"
      >
        <Calendar className="w-4 h-4" />
      </button>
    </div>
  );
}
